<script lang="ts">
	import ArticleCard from "@components/ArticleCard.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Link from "@components/common/Link.svelte";
	import Select from "@components/form/Select.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { getPostsListingPath } from "@core/schema/paths/getPostsListingPath.js";
	import { numberInCircleCharacters } from "@core/utils/unicodes/numberInCircleCharacters.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./PostsListing.translations.json" with { type: "json" };
	import type { PostsListingProps } from "./PostsListingProps.js";

	const { posts, mainPost, archive }: PostsListingProps = $props();
	const { tenant } = getGlobalTemplateProps();
	const [firstPost] = $derived(posts);

	const translate = getTranslate(translations);
</script>

<Layout>
	<Heading
		level={1}
		class="heading-1 my-10 md:my-14 text-center"
		text={archive ? translate("archive", { year: archive.activeYear }) : translate("heading")}
	/>
	{#if archive}
		<nav class="hidden sm:flex flex-wrap gap-2 mb-10">
			{#each archive.yearCounts as [year, count] (year)}
				<Link
					href={getPostsListingPath(tenant, year)}
					variant={ButtonVariant.Outlined}
					size={ButtonSize.Medium}
					class={[archive.activeYear === year && "border-fcrdarkgray text-fcrdarkgray", "gap-4"]}
				>
					<Formatted text={year} />
					<div
						class={[
							"min-w-5 text-center flex justify-center border border-fcrdarkgray text-sm rounded-full",
							archive.activeYear === year && "bg-fcrdarkgray text-white",
						]}
					>
						{count}
					</div>
				</Link>
			{/each}
		</nav>
		<Select
			wrapperClass="sm:hidden w-full mb-5"
			class=" bg-white"
			placeholder={translate("year")}
			onchange={(e: Event & { currentTarget: EventTarget & HTMLSelectElement }): void => {
				location.href = e.currentTarget.value;
			}}
		>
			{#each archive.yearCounts as [year, count] (year)}
				<option value={getPostsListingPath(tenant, year)} selected={archive.activeYear === year}>
					{year}
					<!-- tab -->
					&#160;
					{#if count in numberInCircleCharacters}
						{numberInCircleCharacters[count]}
					{:else}
						(count)
					{/if}
				</option>
			{/each}
		</Select>
	{/if}
	{#if firstPost ?? mainPost}
		{#if mainPost}
			<ArticleCard
				lcp
				headingLevel={2}
				post={mainPost}
				huge
				class={["col-span-full", posts.length > 0 && "mb-14 md:mb-18 md:mt-4"]}
			/>
		{/if}
		<section
			class="grid md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-14 md:gap-y-18 w-fit mx-auto pb-20 sm:pb-30 lg:pb-40"
		>
			{#each posts as post (post.id)}
				<ArticleCard headingLevel={2} {post} huge={false} />
			{/each}
		</section>
	{/if}
	{#if !archive && firstPost}
		<Link
			href={getPostsListingPath(tenant, firstPost.publishedAt.slice(0, 4))}
			variant={ButtonVariant.Outlined}
			size={ButtonSize.Medium}
			class="mb-8 sm:mb-10 md:mb-12 lg:mb-14 xl:mb-16 2xl:mb-18 justify-self-center"
		>
			<Formatted text={translate("loadMore")} />
		</Link>
	{/if}
</Layout>
